import { useCallback, useMemo, useRef, useState } from 'react';
import { RecipientPickerMenu } from './RecipientPickerMenu';
import { RecipientChip, type RecipientChipOption } from './RecipientChip';
import {
  filterByLabel,
  useRecipientSections,
  useRecipientUsers,
  type RecipientOption,
  type RecipientSection,
} from './useRecipientSections';

type SeenOption = { value: string; label: string; image?: string };

/**
 * Wires the sectioned picker onto kula's To/Cc/Bcc selects.
 *
 * Returns props to spread on each of the three, so all of them share one set of
 * sections and one user query — searching in Cc does not re-fetch for Bcc.
 *
 * The menu replaces react-select's own, which is why this goes through
 * `reactSelectProps`: kula manages `options`/`value`/`onChange` on those selects
 * and spreads this escape hatch last, so `components.Menu` wins.
 */
export const useRecipientPicker = (
  jobId?: string,
  menuPortalTarget?: HTMLElement | null
) => {
  const [query, setQuery] = useState('');
  const { jobRoleOptions, orgRoleOptions, tagByUserId } =
    useRecipientSections(jobId);
  const users = useRecipientUsers(query, tagByUserId);

  // Every option the picker has rendered. A chip for a user outside the first
  // page has no other way to resolve its label once the menu closes.
  const seenRef = useRef(new Map<string, SeenOption>());
  const remember = useCallback((options: RecipientOption[]) => {
    options.forEach((option) => {
      seenRef.current.set(option.value, {
        value: option.value,
        label: option.label,
        image: option.image,
      });
    });
    return options;
  }, []);

  const sections = useMemo<RecipientSection[]>(() => {
    const jobRoles = filterByLabel(jobRoleOptions, query);
    const orgRoles = filterByLabel(orgRoleOptions, query);
    remember(jobRoles);
    remember(orgRoles);
    remember(users.options);
    return [
      {
        id: 'job_roles',
        title: 'Job roles',
        count: jobRoleOptions.length,
        options: jobRoles,
      },
      {
        id: 'kula_roles',
        title: 'Kula roles',
        count: orgRoleOptions.length,
        options: orgRoles,
      },
      {
        id: 'users',
        title: 'Users',
        count: users.count,
        options: users.options,
        isLoading: users.isFetching,
      },
    ];
  }, [
    jobRoleOptions,
    orgRoleOptions,
    users.options,
    users.count,
    users.isFetching,
    query,
    remember,
  ]);

  const selectProps = useMemo(
    () => ({
      // `componentsOverride`, NOT `reactSelectProps.components`. kula builds its
      // own components map and spreads `reactSelectProps` last, so a `components`
      // key there replaces the whole map — taking the Control that draws the
      // "To" label and the MultiValue that draws the chips with it.
      componentsOverride: { Menu: RecipientPickerMenu },
      // kula's own chip has one shape for every value; users, job roles and
      // Kula roles each need their own mark. `renderTag` is its supported
      // seam, so the Control and the rest of the chip row stay kula's.
      renderTag: (
        option: RecipientChipOption,
        api: { onRemove: () => void }
      ) => <RecipientChip option={option} onRemove={api.onRemove} />,
      // `onSearchChange`, NOT `reactSelectProps.onInputChange`. kula drives
      // react-select's `inputValue` from its own state and updates it inside
      // its `onInputChange`, then calls this. Passing one through
      // `reactSelectProps` replaces that handler outright — kula's state never
      // advances, so the field stays empty and nothing is ever searched.
      onSearchChange: (value: string) => setQuery(value),
      reactSelectProps: {
        // Where the menu portals is the caller's call. Left undefined, kula sends
        // it to `document.body` — right on a Chakra surface, but dead inside a
        // kula Drawer: that is a Radix Dialog with `modal` on, so everything
        // outside the dialog gets `pointer-events: none` (no clicks, no scroll)
        // and the panel stacks above it at z-index 1500. Such a caller passes a
        // node inside the drawer instead, keeping the menu floating but in-dialog.
        ...(menuPortalTarget === undefined ? {} : { menuPortalTarget }),
        // The menu renders the sections itself, so react-select's own filtering
        // and empty-state would only fight it.
        filterOption: () => true,
        // Read by the menu off `selectProps`.
        recipientPicker: { sections, optionByValue: seenRef.current },
      },
    }),
    [sections, menuPortalTarget]
  );

  return { selectProps, sections, isSearching: users.isFetching };
};
